import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReferenceDiscount } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceDiscount';
import { ReferenceType } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferencesStockRequest';
import { ChevronDownIcon, ChevronUpIcon, TagsIcon } from 'assets/icons';
import { calculatePriceAfterDiscount, getDiscountColor, getDiscountTextStyle } from 'components/Discount';
import { getDiscountText, getVatExcludedText } from 'components/ReferencePriceSection/ReferencePriceSectionUtils';
import { ReferencePriceType } from 'domains/references';
import { theme } from 'styles';
import { Flex, Icon, MarginBox, Text } from 'UI';
import { textFormatter } from 'utils';

const ClientDiscountView = ({
  displayDetailedView,
  setDisplayDetailedView,
  priceData,
  discount,
  referenceType,
  isNoLongerAvailable,
  isAvailableSoon,
}: {
  displayDetailedView: boolean;
  setDisplayDetailedView: (display: boolean) => void;
  priceData: ReferencePriceType | undefined;
  discount: ReferenceDiscount | undefined;
  referenceType: ReferenceType;
  isNoLongerAvailable?: boolean;
  isAvailableSoon?: boolean;
}) => {
  const { t } = useTranslation();
  const promotionDiscountValue = discount?.discount;

  if (referenceType === 'TIRE' && !isNoLongerAvailable && !isAvailableSoon) {
    return null;
  }

  const renderDetailedView = () => (
    <Flex direction="column">
      <Flex direction="row">
        <Flex minWidth={159} maxWidth={159}>
          {promotionDiscountValue && (
            <Icon IconComponent={TagsIcon} size={16} mt={2} mr={5} color={getDiscountColor(discount)} noPointer />
          )}
          <Text type="sm" color="grey35" data-cy="discount-promotion">
            {getDiscountText(t, priceData?.garageView?.discountRate)}
          </Text>
        </Flex>
        <Icon
          IconComponent={ChevronUpIcon}
          size={16}
          onClick={() => setDisplayDetailedView(false)}
          hoverFill={theme.color.grey20}
          mt={1}
          ml={5}
        />
      </Flex>
      {promotionDiscountValue && (
        <Flex>
          <Text type="sm" displayStyle={getDiscountTextStyle(discount)}>
            {textFormatter.formatPercentDecimal(promotionDiscountValue)}
          </Text>
          <MarginBox mr={5} />
          <Text type="sm" displayStyle={getDiscountTextStyle(discount)}>
            {t('common.discount.additional_discount', 'Additional discount')}
          </Text>
        </Flex>
      )}
      {priceData && (
        <Text type="sm" color="grey35" data-cy="price-vat-excl">
          {getVatExcludedText(t, calculatePriceAfterDiscount(discount, priceData, false), priceData?.currency)}
        </Text>
      )}
    </Flex>
  );
  const renderSummaryView = () => (
    <Flex direction="column">
      <Flex direction="row">
        <Flex minWidth={159} maxWidth={159}>
          {promotionDiscountValue && (
            <Icon IconComponent={TagsIcon} size={16} mt={2} mr={5} color={getDiscountColor(discount)} noPointer />
          )}
          <Text type="sm" color="grey35">
            {t('common.discount.discount_code', 'DiscountCode: {{discount_code}}', {
              discount_code: priceData?.clientView?.discountCode,
            })}
          </Text>
        </Flex>
        {!isAvailableSoon && !isNoLongerAvailable && (
          <Icon
            IconComponent={ChevronDownIcon}
            size={16}
            hoverFill={theme.color.grey20}
            onClick={() => setDisplayDetailedView(true)}
            mt={1}
            ml={5}
          />
        )}
      </Flex>
      <MarginBox mt={promotionDiscountValue ? 40 : 20} />
    </Flex>
  );

  return displayDetailedView ? renderDetailedView() : renderSummaryView();
};

export default ClientDiscountView;
